<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Component
} from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useCookies } from 'vue3-cookies'
import { ElBadge, ElIcon } from 'element-plus'
import { useRoute } from 'vue-router'
import { Close, Operation } from '@/shared/icons'

/**
 * The drawer that slides in from the right, opened by a handle pinned to the edge of the
 * screen — for something beside the page rather than in front of it. A dialog the user has
 * to answer is a `Modal`.
 *
 * The content is the default slot; `count` puts a badge on the handle. `once` remembers, in
 * a cookie keyed by the route, that this panel has already been dismissed, so a panel that
 * offers help does not reopen on every visit — a panel the user opens themselves does not
 * want it.
 */
const emit = defineEmits<{
  open: []
  close: []
}>()

const props = withDefaults(
  defineProps<{
    clickNotClose?: boolean
    showByDefault?: boolean
    backdrop?: boolean
    once?: boolean
    count?: number
    id?: string
    /**
     * The glyph on the handle: pass a component from `@/shared/icons`, never a globally
     * registered icon name. A `src/shared` component that resolves an icon by string is
     * what keeps the app-wide registration of every element-plus icon undeletable.
     */
    icon?: Component
    buttonTop?: number
    maxWidth?: number
  }>(),
  {
    clickNotClose: false,
    showByDefault: false,
    backdrop: true,
    once: false,
    count: 0,
    id: '',
    icon: () => Operation,
    buttonTop: 80,
    maxWidth: 250
  }
)

const route = useRoute()
const { cookies } = useCookies()

const cookiesKey = computed(() => route.path.replaceAll('/', ''))

/**
 * Whether this route's panel has already been dismissed once.
 *
 * Held in a ref rather than read off `document.cookie` inside the computed below. Cookies
 * are not reactive, so a computed over one caches whatever it saw first and has no reason
 * ever to recompute — the cached default and the actual cookie then disagree, and the
 * watcher that writes the default into `show` writes a stale one over the user's own
 * choice. Re-read when the route changes, which is the only thing the key depends on.
 */
const dismissed = ref(!!cookies.get(cookiesKey.value))

watch(cookiesKey, (key) => {
  dismissed.value = !!cookies.get(key)
})

const resolvedShowByDefault = computed(
  () => (props.showByDefault && !props.once) || (props.once && !dismissed.value)
)

const show = ref(resolvedShowByDefault.value)
const rightPanel = ref<HTMLElement | null>(null)

const panelContent = ref<HTMLElement | null>(null)

onClickOutside(
  panelContent,
  () => {
    if (show.value && !props.clickNotClose) show.value = false
  },
  // Clicks inside any right panel stay internal; stopped clicks must not close one.
  { ignore: ['.rightPanel'], capture: false }
)

function toggle() {
  show.value = !show.value
  if (!show.value) {
    cookies.set(cookiesKey.value, 'true')
    dismissed.value = true
  }
}

function insertToBody() {
  const body = document.querySelector('body') as HTMLElement
  if (rightPanel.value) {
    body.insertBefore(rightPanel.value, body?.firstChild)
  }
}

watch(resolvedShowByDefault, (value) => {
  show.value = value
})

watch(show, (value) => {
  if (value) {
    document.body.classList.add('showRightPanel')
    emit('open')
  } else {
    document.body.classList.remove('showRightPanel')
    emit('close')
  }
})

/**
 * A new `id` means a different subject, so the panel goes back to whatever it opens as. Do
 * not write this as a flip: a watcher only fires on change, so flipping would *close* an
 * open panel and *open* a closed one, which is not a reset by any reading.
 */
watch(
  () => props.id,
  () => {
    show.value = resolvedShowByDefault.value
  }
)

const cssVars = computed(() => ({
  '--min-label-width': `${props.maxWidth}px`
}))

onMounted(() => {
  insertToBody()
})

onBeforeUnmount(() => {
  document.body.classList.remove('showRightPanel')
  rightPanel.value?.remove()
})
</script>

<template>
  <div
    ref="rightPanel"
    class="rightPanel-container"
    :class="{ show: show }"
    :style="cssVars"
  >
    <div v-if="backdrop" class="rightPanel-background" />
    <div ref="panelContent" class="rightPanel">
      <button
        type="button"
        class="handle-button"
        :style="{ top: buttonTop + 'px' }"
        @click.stop="toggle"
      >
        <ElBadge v-if="count >= 1" class="handle-button__badge" :value="count">
          <ElIcon class="handle-button__icon">
            <Close v-if="show" />
            <component :is="icon" v-else />
          </ElIcon>
        </ElBadge>
        <ElIcon v-else class="handle-button__icon">
          <Close v-if="show" />
          <component :is="icon" v-else />
        </ElIcon>
      </button>
      <div class="rightPanel-items">
        <slot />
      </div>
    </div>
  </div>
</template>

<style>
.showRightPanel {
  overflow: hidden;
  position: relative;
  width: calc(100% - 15px);
}
</style>

<style lang="scss" scoped>
.rightPanel-container {
  overflow: visible;
}

.rightPanel-background {
  position: fixed;
  top: 0;
  left: 0;
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.7, 0.3, 0.1, 1);
  background: rgba(0, 0, 0, 0.2);
  z-index: -1;
}

.rightPanel {
  width: 100%;
  max-width: var(--min-label-width);
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  overflow: visible;
  box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.05);
  transition: all 0.25s cubic-bezier(0.7, 0.3, 0.1, 1);
  transform: translate(100%);
  background: var(--el-bg-color);
  z-index: 1000;
}

.rightPanel-items {
  overflow-y: scroll;
  overflow-x: hidden;
  height: 100vh;
}

.show {
  transition: all 0.3s cubic-bezier(0.7, 0.3, 0.1, 1);

  .rightPanel-background {
    z-index: 999;
    opacity: 1;
    width: 100%;
    height: 100%;
  }

  .rightPanel {
    transform: translate(0);
  }
}

.handle-button {
  width: 48px;
  height: 48px;
  position: absolute;
  left: -48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  border-radius: 6px 0 0 6px !important;
  z-index: 1002;
  pointer-events: auto;
  cursor: pointer;
  color: var(--el-color-white);
  line-height: 0;
  background-color: var(--el-color-primary);
  border: none;
  padding: 0;
  font: inherit;

  :deep(.handle-button__badge) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    line-height: 0;
  }

  :deep(.handle-button__badge .el-badge__content) {
    top: 4px;
    right: 4px;
  }

  :deep(.handle-button__icon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: 0;
    font-size: 24px;
    line-height: 1;
    vertical-align: middle;
  }

  :deep(.handle-button__icon svg) {
    width: 1em;
    height: 1em;
  }
}
</style>
